import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { ExternalLinks } from '../../../../core/constants/external-links';
import { UserMembershipStatus } from '../../../../core/enums/users/membership-status';
import { AuthenticatedUser } from '../../../../core/models/authenticated-user';
import { DateService } from '../../../../core/services/date/date.service';

@Component({
    selector: 'app-membership-chip',
    templateUrl: './membership-chip.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatButtonModule],
})
export class MembershipChipComponent {
    @Input() isCollapsed!: boolean;
    @Input() user: AuthenticatedUser | null = null;

    readonly externalLinks = ExternalLinks;

    constructor(private readonly dateService: DateService) {}

    /**
     * The number of days to define the "recently expired" window for a membership. Negative value
     * indicates that the membership is expired.
     *
     * A membership is considered recently expired if it ended within this number of days (>= -90).
     * A membership is considered expired beyond recent if it ended more than this number of days ago (< -90).
     */
    readonly recentlyExpiredMembershipWindowInDays = -90;

    /**
     * Checks to see if the membership of the user is active.
     * @returns {boolean} True if the membership is active, false otherwise.
     */
    displayMembershipActive(): boolean {
        return this.user?.membershipStatus == UserMembershipStatus.Current;
    }

    /**
     * Checks to see if the membership of the user is new or beyond recently expired.
     * @returns {boolean} True if the membership is new or expired beyond recent, false otherwise.
     */
    displayBecomeAMember(): boolean {
        if (!this.user) {
            return false;
        }

        const { membershipStatus } = this.user;

        return (
            membershipStatus == null ||
            membershipStatus === UserMembershipStatus.New ||
            this.isMembershipExpiredBeyondRecent()
        );
    }

    /**
     * Checks to see if the membership of the user is in grace period or recently expired.
     * @returns {boolean} True if the membership is in grace period or recently expired, false otherwise.
     */
    displayRenewMembership(): boolean {
        return (
            this.user?.membershipStatus == UserMembershipStatus.Grace ||
            this.isMembershipRecentlyExpired()
        );
    }

    /**
     * Checks if the membership is recently expired (within the last 90 days).
     */
    private isMembershipRecentlyExpired(): boolean {
        const daysExpired = this.getDaysSinceMembershipEnded();
        return (
            daysExpired !== null &&
            daysExpired >= this.recentlyExpiredMembershipWindowInDays &&
            daysExpired < 0
        );
    }

    /**
     * Checks if the membership expired more than 90 days ago.
     */
    private isMembershipExpiredBeyondRecent(): boolean {
        const daysExpired = this.getDaysSinceMembershipEnded();

        return (
            daysExpired !== null &&
            daysExpired < this.recentlyExpiredMembershipWindowInDays
        );
    }

    /**
     * Calculates how many days have passed since the membership end date.
     * Returns null if no membership end date exists.
     */
    private getDaysSinceMembershipEnded(): number | null {
        if (!this.user?.membershipEndDate) {
            return null;
        }
        return this.dateService.calculateDaysBetweenDates(
            new Date(),
            this.user.membershipEndDate,
        );
    }
}
